import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'dnd-saved-characters';

const generateId = () => Math.random().toString(36).substr(2, 9);

export const useCharacters = () => {
  const [characters, setCharacters] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading characters:', e);
    }
    return [];
  });

  // Save to localStorage on every change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(characters));
    } catch (e) {
      console.error('Error saving characters:', e);
    }
  }, [characters]);

  // Add character
  const addCharacter = useCallback((character) => {
    const newCharacter = {
      id: generateId(),
      name: character.name,
      xp: Math.max(0, parseInt(character.xp, 10) || 0),
      maxHp: character.maxHp,
      ac: character.ac || null, // Armor Class
      baseInitiative: character.baseInitiative || null,
      initiativeBonus: typeof character.initiativeBonus === 'number' ? character.initiativeBonus : 0,
      speed: typeof character.speed === 'number' ? character.speed : 30,
      hitDice: character.hitDice && character.hitDice.total > 0 ? { total: character.hitDice.total, sides: character.hitDice.sides || 8 } : null,
      spellSlots: character.spellSlots || {}, // { [level:number]: { max:number, used:number } }
      defaultStatuses: character.defaultStatuses || [],
      isFamiglio: !!character.isFamiglio,
      famiglioDi: character.famiglioDi || null,
      createdAt: new Date().toISOString(),
    };
    
    setCharacters(prev => [...prev, newCharacter]);
    return newCharacter;
  }, []);

  // Update character
  const updateCharacter = useCallback((id, updates) => {
    setCharacters(prev => prev.map(c => 
      c.id === id ? { ...c, ...updates } : c
    ));
  }, []);

  // Delete character
  const deleteCharacter = useCallback((id) => {
    setCharacters(prev => prev.filter(c => c.id !== id));
  }, []);

  // Duplicate character: name becomes "Name 2", "Name 3", ...
  const duplicateCharacter = useCallback((id) => {
    const original = characters.find(c => c.id === id);
    if (!original) return null;
    const baseName = original.name.replace(/\s+\d+$/, '').trim() || original.name;
    const sameBase = characters.filter(c => {
      const name = c.name.trim();
      if (name === baseName) return true;
      const m = name.match(new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+(\\d+)$`));
      return !!m;
    });
    const numbers = sameBase.map(c => {
      if (c.name.trim() === baseName) return 1;
      const m = c.name.trim().match(new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+(\\d+)$`));
      return m ? parseInt(m[1], 10) : 0;
    }).filter(n => n >= 1);
    const nextNum = numbers.length === 0 ? 2 : Math.max(...numbers) + 1;
    const newName = `${baseName} ${nextNum}`;
    const copy = {
      ...original,
      id: generateId(),
      name: newName,
      createdAt: new Date().toISOString(),
    };
    setCharacters(prev => [...prev, copy]);
    return copy;
  }, [characters]);

  // Get character by ID
  const getCharacter = useCallback((id) => {
    return characters.find(c => c.id === id) || null;
  }, [characters]);

  // Export characters to JSON
  const exportCharacters = useCallback(() => {
    const data = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      characters: characters,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `i miei personaggi.json`;
    a.click();
    URL.revokeObjectURL(url);
    return true;
  }, [characters]);

  // Import characters from JSON
  const importCharacters = useCallback((jsonData, mode = 'merge') => {
    try {
      const data = JSON.parse(jsonData);
      let importedChars = [];
      
      // Handle both old format (array) and new format (object with characters array)
      if (Array.isArray(data)) {
        importedChars = data;
      } else if (data.characters && Array.isArray(data.characters)) {
        importedChars = data.characters;
      } else {
        return { success: false, error: 'Formato file non valido' };
      }

      // Validate imported characters
      const validChars = importedChars.filter(c => c.name && c.maxHp);
      if (validChars.length === 0) {
        return { success: false, error: 'Nessun personaggio valido trovato' };
      }

      // Assign new IDs to avoid conflicts
      const newChars = validChars.map(c => ({
        ...c,
        id: generateId(),
        importedAt: new Date().toISOString(),
      }));

      if (mode === 'replace') {
        setCharacters(newChars);
      } else {
        // Merge: add only characters that don't exist by name
        setCharacters(prev => {
          const existingNames = new Set(prev.map(c => c.name.toLowerCase()));
          const uniqueNew = newChars.filter(c => !existingNames.has(c.name.toLowerCase()));
          return [...prev, ...uniqueNew];
        });
      }

      return { success: true, count: newChars.length };
    } catch (e) {
      console.error('Error importing characters:', e);
      return { success: false, error: 'Errore durante l\'importazione' };
    }
  }, []);

  return {
    characters,
    addCharacter,
    updateCharacter,
    deleteCharacter,
    duplicateCharacter,
    getCharacter,
    exportCharacters,
    importCharacters,
  };
};
